import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  format,
  isSameDay,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  subDays,
} from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { Calendar } from 'src/components/ui/calendar';
import { buttonVariants } from 'src/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { cn } from 'src/lib/utils';

// Formats a { from, to } range for the trigger's own label — deliberately
// short (day + short month, year only when it isn't the current one) since
// this sits inline next to a SearchInput in a filter row, not in its own
// form field.
const formatShort = (d) =>
  format(
    d,
    d.getFullYear() === new Date().getFullYear() ? 'd MMM' : 'd MMM yyyy'
  );

const formatRange = (range) => {
  if (!range?.from) return null;
  const from = formatShort(range.from);
  if (!range.to || isSameDay(range.from, range.to)) return from;
  return `${from} – ${formatShort(range.to)}`;
};

// The popover's own big header label — "Oct 25 – Oct 31", no year clutter,
// live-updates as the draft selection changes (distinct from the trigger's
// own shorter formatRange above).
const formatHeaderRange = (range) => {
  if (!range?.from) return 'Select start and end date';
  const from = format(range.from, 'MMM d');
  if (!range.to || isSameDay(range.from, range.to)) return from;
  return `${from} – ${format(range.to, 'MMM d')}`;
};

const PRESETS = [
  {
    label: 'This Week',
    getRange: () => {
      const now = new Date();
      return { from: startOfWeek(now), to: endOfWeek(now) };
    },
  },
  {
    label: 'Last Week',
    getRange: () => {
      const lastWeek = subWeeks(new Date(), 1);
      return { from: startOfWeek(lastWeek), to: endOfWeek(lastWeek) };
    },
  },
  {
    label: 'Last 7 Days',
    getRange: () => ({ from: subDays(new Date(), 6), to: new Date() }),
  },
  {
    label: 'Current Month',
    getRange: () => {
      const now = new Date();
      return { from: startOfMonth(now), to: endOfMonth(now) };
    },
  },
  {
    label: 'Next Month',
    getRange: () => {
      const nextMonth = addMonths(new Date(), 1);
      return { from: startOfMonth(nextMonth), to: endOfMonth(nextMonth) };
    },
  },
];

/**
 * A single-trigger date-range filter. The popover pairs a left rail of quick
 * presets (This Week / Last Week / Last 7 Days / Current Month / Next Month
 * / Reset) with a single-month range Calendar and a live "Oct 25 – Oct 31"
 * header, confirmed via Cancel/OK. Controlled: pass `value` ({ from, to } |
 * undefined) and `onChange` — picking a preset or a calendar day only updates
 * an internal draft, so `onChange` fires once, on OK, and a Cancel (or
 * closing without confirming) discards whatever was picked that session.
 */
const DateRangePicker = ({
  value,
  onChange,
  placeholder,
  className,
  align,
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const label = formatRange(value);

  // Re-seed the draft from the applied value every time the popover opens,
  // so a prior Cancel (or stale draft) never leaks into the next session.
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const handleConfirm = () => {
    onChange(draft);
    setOpen(false);
  };

  const handleCancel = () => setOpen(false);

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(undefined);
  };

  return (
    <div className={cn('relative w-full', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'inline-flex w-full items-center gap-2 rounded-lg border border-input bg-card py-2 pl-3 pr-3 text-[12.5px] text-foreground-strong outline-none transition-[border-color,box-shadow] hover:border-primary/50 focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]',
              label && 'pr-8'
            )}
          >
            <CalendarIcon
              className="h-[15px] w-[15px] shrink-0 text-muted-foreground"
              strokeWidth={1.8}
            />
            <span className={cn('truncate', !label && 'text-muted-foreground')}>
              {label || placeholder}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align={align}>
          <div className="flex gap-1 p-3.5">
            {/* Preset rail */}
            <div className="flex w-[104px] flex-shrink-0 flex-col gap-1 pt-[38px]">
              {PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setDraft(preset.getRange())}
                  className="rounded-full bg-muted px-2.5 py-[7px] text-left text-[11px] font-medium leading-tight text-foreground-strong transition-colors hover:bg-border"
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setDraft(undefined)}
                className="mt-1 rounded-full bg-muted px-2.5 py-[7px] text-left text-[11px] font-medium leading-tight text-foreground-strong transition-colors hover:bg-border"
              >
                Reset
              </button>
            </div>

            {/* Header + calendar + footer */}
            <div className="flex min-w-[216px] flex-col">
              <div className="mb-2">
                <div className="text-[9px] font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                  Select date range
                </div>
                <div className="mt-0.5 text-[15px] font-bold tracking-[-0.02em] text-foreground-strong">
                  {formatHeaderRange(draft)}
                </div>
              </div>

              <Calendar
                mode="range"
                numberOfMonths={1}
                defaultMonth={draft?.from || value?.from}
                selected={draft}
                onSelect={setDraft}
                initialFocus
                className="p-0"
                classNames={{
                  caption:
                    'flex justify-between items-center px-0.5 pt-0.5 mb-1',
                  caption_label:
                    'text-[12px] font-bold tracking-[-0.01em] text-foreground-strong',
                  nav: 'flex items-center gap-0.5',
                  nav_button: cn(
                    buttonVariants({ variant: 'outline' }),
                    'h-5 w-5 bg-transparent p-0 opacity-50 hover:opacity-100'
                  ),
                  nav_button_previous: 'static',
                  nav_button_next: 'static',
                  table: 'w-full border-collapse space-y-0.5',
                  head_cell:
                    'text-muted-foreground rounded-md w-7 font-semibold text-[9px] uppercase',
                  row: 'flex w-full mt-0.5',
                  cell: 'h-7 w-7 text-center text-[11.5px] p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
                  day: cn(
                    buttonVariants({ variant: 'ghost' }),
                    'h-7 w-7 p-0 text-[11.5px] font-normal aria-selected:opacity-100'
                  ),
                }}
              />

              <div className="flex items-center justify-end gap-3.5 pt-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="text-[11px] font-semibold uppercase tracking-[0.03em] text-primary hover:text-primary/80"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="text-[11px] font-semibold uppercase tracking-[0.03em] text-primary hover:text-primary/80"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      {label && (
        <button
          type="button"
          aria-label="Clear date range"
          onClick={handleClear}
          className="absolute right-2 top-1/2 flex h-[18px] w-[18px] -translate-y-1/2 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-border hover:text-foreground-strong"
        >
          <X className="h-[11px] w-[11px]" strokeWidth={2.2} />
        </button>
      )}
    </div>
  );
};

DateRangePicker.propTypes = {
  value: PropTypes.shape({
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date),
  }),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  align: PropTypes.oneOf(['start', 'center', 'end']),
};

DateRangePicker.defaultProps = {
  value: undefined,
  placeholder: 'Filter by date range',
  className: '',
  align: 'end',
};

export default DateRangePicker;
